/* Calmy storefront core: header, navigation, search, sort/filter, cart drawer.
   Vanilla, no dependencies, no external libraries.

   This file owns two behaviours that CSS cannot express:
     1. the portal, which guarantees every overlay is positioned against the viewport
     2. the scroll lock, which pins the page while an overlay is open
   Everything visual about those overlays lives in calmy-core.css. */
window.Calmy = window.Calmy || {};
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  'use strict';
  var C = window.Calmy;

  /* WHICH CART IS IN CHARGE. Written on <body> by layout/theme.liquid from the
     "Cart provider" theme setting. Under Kaching the theme owns no drawer: it
     does not portal one, does not open one and does not trap focus in one. The
     only cart work left here is keeping the header count truthful, because that
     element belongs to the theme and no app updates it. */
  C.cartProvider = (document.body && document.body.getAttribute('data-cart-provider')) || 'kaching';
  C.usesThemeCart = C.cartProvider === 'calmy';

  C.moneyFormat = '${{amount}}';
  C.money = function (cents) {
    var n = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(C.moneyFormat).replace(/\{\{\s*amount\s*\}\}/, n);
  };
  function withWidth(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Polite announcer shared by the cart and any other async update. */
  var live;
  function announce(msg) {
    if (!live) {
      live = document.createElement('div');
      live.className = 'calmy-sr';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(function () { live.textContent = msg; }, 60);
  }
  C.announce = announce;

  /* ---------------- Portal ----------------
     Every overlay and drawer is authored inside a section, which sits inside
     .shopify-section and .page-wrapper. Any ancestor that establishes a containing block -
     a transform, a filter, contain, or a view-transition - turns position:fixed into
     position-relative-to-that-ancestor. That is what made the mobile cart drawer render as
     a narrow strip with the sticky header showing above it.

     Moving these elements to <body> removes the whole class of bug: they are then
     positioned against the viewport and sit on the z-index ladder in calmy-core.css.
     The collection filter drawer is in the list for the same reason.

     Re-entrant: in the theme editor a section reload re-renders the originals inside the
     section, so any previously portaled copy is removed before the fresh one is appended. */
  var PORTAL = [
    '[data-calmy-overlay]',
    '[data-calmy-nav]',
    '[data-calmy-search]',
    /* '[data-calmy-cart]' is added below only when the theme owns the cart. */
    /* The Club drawer is authored inside the header section, and the header's
       group wrapper is now position:sticky with a z-index — which makes it a
       stacking context. Left inside it, the drawer's z-index:1001 is scoped to
       that context and the z-index:1000 backdrop, which lives at the root,
       paints straight over it: the whole drawer rendered behind the scrim.
       Portalling it to <body> is the same fix the other three already use. */
    '[data-calmy-club]'
    /* [data-calmy-account] used to be portaled here too. The account entry
       modal is gone - it is a page now (sections/calmy-account-entry.liquid),
       which needs no portal because it is not inside the header's stacking
       context. */
    /* The collection filters used to be portaled here too. They are no longer
       always a drawer - from 1000px they are a sidebar column in the grid, and
       a sidebar cannot live at the end of <body>. calmy-filters.js portals them
       only while they are behaving as a sheet, and puts them back on the way
       up. */
  ];
  if (C.usesThemeCart) PORTAL.push('[data-calmy-cart]');

  function portal() {
    var body = document.body;
    PORTAL.forEach(function (sel) {
      var nodes = Array.prototype.slice.call(document.querySelectorAll(sel));
      if (!nodes.length) return;
      var incoming = nodes.filter(function (n) { return n.parentElement !== body; });
      if (!incoming.length) return;
      nodes.forEach(function (n) {
        if (n.parentElement === body && n.parentNode) n.parentNode.removeChild(n);
      });
      incoming.forEach(function (n) { body.appendChild(n); });
    });
  }

  /* ---------------- Scroll lock ----------------
     overflow:hidden on <body> does not stop iOS Safari scrolling the page behind a fixed
     overlay, so the body is pinned with position:fixed at a negative offset and the offset
     is restored on close. Guarded so opening a second drawer over the first cannot capture
     a scroll position of 0 and drop the shopper back at the top of the page. */
  var lockedY = 0;
  var isLocked = false;
  function lockScroll() {
    if (isLocked) return;
    isLocked = true;
    lockedY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = -lockedY + 'px';
    document.body.classList.add('calmy-lock');
  }
  function unlockScroll() {
    if (!isLocked) return;
    isLocked = false;
    document.body.classList.remove('calmy-lock');
    document.body.style.top = '';
    window.scrollTo(0, lockedY);
  }

  /* ---------------- Panels: focus trap, focus restore, aria state ---------------- */
  var openPanel = null;
  var lastTrigger = null;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function overlay() { return document.querySelector('[data-calmy-overlay]'); }

  function focusables(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (n) {
      return n.offsetParent !== null || n === document.activeElement;
    });
  }

  function setTriggerState(panel, expanded) {
    if (!panel || !panel.id) return;
    document.querySelectorAll('[aria-controls="' + panel.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function trap(e) {
    if (e.key !== 'Tab' || !openPanel) return;
    var items = focusables(openPanel);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  C.close = function () {
    if (openPanel) {
      openPanel.classList.remove('is-on');
      openPanel.setAttribute('inert', '');
      setTriggerState(openPanel, false);
    }
    var o = overlay();
    if (o) o.classList.remove('is-on');
    unlockScroll();
    document.removeEventListener('keydown', trap, true);
    openPanel = null;
    if (lastTrigger) { try { lastTrigger.focus({ preventScroll: true }); } catch (e) {} lastTrigger = null; }
  };

  C.open = function (selector, trigger) {
    var el = document.querySelector(selector);
    if (!el) return;
    if (openPanel && openPanel !== el) {
      openPanel.classList.remove('is-on');
      openPanel.setAttribute('inert', '');
      setTriggerState(openPanel, false);
    }
    openPanel = el;
    lastTrigger = trigger || null;
    el.removeAttribute('inert');
    el.classList.add('is-on');
    setTriggerState(el, true);
    var o = overlay();
    if (o) o.classList.add('is-on');
    lockScroll();
    document.addEventListener('keydown', trap, true);
    window.setTimeout(function () {
      /* The search panel opens ON ITS FIELD, not on whatever happens to be the
         first focusable in it: a shopper who opened search wants to type, and
         the close button sitting earlier in the DOM would otherwise take it.
         Every other panel keeps the first-focusable behaviour. */
      var target = el.querySelector('[data-calmy-search-input]');
      if (!target) {
        var items = focusables(el);
        target = items.length ? items[0] : null;
      }
      if (target) {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    }, 240);
  };

  document.addEventListener('click', function (e) {
    /* A trigger that is also a real link - the account icon is one - must keep
       behaving like a link when the visitor asks for a link: a middle click, or
       a click held with cmd/ctrl/shift/alt, opens the href in a new tab or
       window. Swallowing those would make the anchor a worse button than a
       button. An ordinary left click still opens the panel. */
    var mod = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0);
    var t = e.target.closest && e.target.closest('[data-calmy-open]');
    if (t) {
      if (mod && t.tagName === 'A' && t.getAttribute('href')) return;
      e.preventDefault();
      C.open(t.getAttribute('data-calmy-open'), t);
      return;
    }
    if (e.target.closest && e.target.closest('[data-calmy-close]')) { e.preventDefault(); C.close(); }
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && openPanel) C.close(); });

  /* Sticky product bar submits the real product form. Kept here rather than as an inline
     onclick so the markup stays CSP friendly and the behaviour lives in one place. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-calmy-sticky-atc]');
    if (!b) return;
    var scope = b.closest('[data-calmy-product]');
    var form = scope && scope.querySelector('form[data-calmy-form]');
    if (!form) return;
    e.preventDefault();
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  });

  /* ---------------- Predictive search ----------------
     assets/calmy-search.js owns this panel: it handles the grid, recent
     searches, collections and pages, keyboard navigation and the live region.
     It claims the box with __calmySearchV2.

     What follows is the fallback for a page where that file did not load. Both
     bind to the same input, so without this guard two listeners raced to write
     the same element and whichever resolved last won - which is how the panel
     could show rows one keystroke and cards the next. */
  function initSearch() {
    var box = document.querySelector('[data-calmy-search]');
    if (!box || box.__calmySearch || box.__calmySearchV2) return;
    box.__calmySearch = true;
    var input = box.querySelector('[data-calmy-search-input]');
    var out = box.querySelector('[data-calmy-search-results]');
    if (!input || !out) return;
    var defaultHTML = out.innerHTML;
    var timer, controller;
    /* NEWEST QUERY WINS.

       abort() alone is not enough: a response can already be in flight to the
       microtask queue when the next keystroke fires, resolve after it, and
       repaint the older result over the newer one. Every request takes a
       sequence number and only the latest one is allowed to write. */
    var seq = 0;

    /* A COLLECTION CARD, BUILT FROM suggest.json.

       The same shape the Liquid renders for the empty state - .ccard--plain,
       image on top, words beneath - so typing swaps the contents of the grid
       rather than its layout. Only the fields the endpoint actually returns are
       drawn: no invented badge, no invented rating, no placeholder subtitle. */
    function card(p) {
      var img = (p.featured_image && p.featured_image.url) || p.image || '';
      var media = img
        ? '<span class="ccard__media"><img class="ccard__img" src="' + esc(withWidth(img, 600)) +
          '" alt="" loading="lazy"></span>'
        : '<span class="ccard__media"><span class="ccard__empty"></span></span>';

      /* compare_at_price_min is what suggest.json carries; it is only a sale
         when it is genuinely above the price. */
      /* suggest.json gives price as a DECIMAL STRING - "39.95" - while
         C.money takes cents. Converted once, here, or every price renders as
         a hundredth of itself. */
      function mny(v) {
        var n = typeof v === 'string' ? parseFloat(v) : v;
        return isFinite(n) ? C.money(Math.round(n * 100)) : '';
      }
      var price = '';
      if (p.price != null) {
        var was = p.compare_at_price_min != null ? p.compare_at_price_min : p.compare_at_price;
        if (was != null && parseFloat(was) > parseFloat(p.price)) {
          price += '<del class="ccard__was">' + esc(mny(was)) + '</del>';
        }
        price += '<span class="ccard__now">' + esc(mny(p.price)) + '</span>';
      }

      /* The card is a <div> with an overlay link, matching what the Liquid
         snippet renders: the cart button is a sibling of the link rather than
         a button nested inside an anchor. The button carries the handle and
         calmy-cards.js resolves the variant on the first press. */
      var live = p.available !== false;
      var add = '<button type="button" class="ccard__add' + (live ? '' : ' ccard__add--choose') + '"' +
        ' data-calmy-cardadd' +
        (live ? ' data-product-handle="' + esc(p.handle) + '"' : '') +
        ' data-product-url="' + esc(p.url) + '"' +
        ' aria-label="' + esc((live ? 'Add to cart: ' : 'View product: ') + p.title) + '">' +
        '<span class="ccard__add-g ccard__add-g--rest" aria-hidden="true"><svg class="cico" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.75 4h2.2l2.3 10.4h9.9l2.1-7.4H6.1"/><circle cx="9.5" cy="18.75" r="1.5"/><circle cx="16.5" cy="18.75" r="1.5"/></svg></span>' +
        '<span class="ccard__add-g ccard__add-g--done" aria-hidden="true"><svg class="cico" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' +
        '<span class="ccard__add-g ccard__add-g--busy" aria-hidden="true"><svg class="cico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" opacity=".25"/><path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5"/></svg></span>' +
        '</button>';

      return '<div class="ccard calmy ccard--plain">' +
        '<div class="ccard__inner">' +
          '<a class="ccard__link" href="' + esc(p.url) + '"><span class="calmy-sr">' + esc(p.title) + '</span></a>' +
          media.replace('</span>', add + '</span>') +
          '<span class="ccard__foot">' +
            '<span class="ccard__t">' + esc(p.title) + '</span>' +
            '<span class="ccard__sub"></span>' +
            '<span class="ccard__p">' + price + '</span>' +
          '</span>' +
        '</div></div>';
    }

    function render(products, q, more) {
      if (!products.length) {
        out.innerHTML = '<p class="csearch__msg">' +
          esc(CT('search_no_matches', 'No matches for \u201c[query]\u201d. Try a different word.').split('[query]').join(q)) +
          '</p>';
        return;
      }
      var html = '<div class="cgrid csearch__grid">' + products.map(card).join('') + '</div>';
      /* Only where there is more to see. Offering "view all" on a complete set
         of results sends the shopper to the same products a second time. */
      if (more) {
        html += '<a class="csearch__all" href="/search?q=' + encodeURIComponent(q) + '">' +
          esc(CT('search_view_all', 'View all results')) + '</a>';
      }
      out.innerHTML = html;
    }

    var LIMIT = 8;

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      /* Clearing the field restores the suggestions exactly as Liquid rendered
         them, and cancels anything still in flight so a late response cannot
         paint over them. */
      if (q.length < 2) {
        seq++;
        if (controller) controller.abort();
        out.innerHTML = defaultHTML;
        return;
      }
      timer = setTimeout(function () {
        if (controller) controller.abort();
        controller = new AbortController();
        var mine = ++seq;
        out.innerHTML = '<p class="csearch__msg">' + esc(CT('search_searching', 'Searching\u2026')) + '</p>';
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product&resources[limit]=' + (LIMIT + 1), { signal: controller.signal })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            if (mine !== seq) return;            /* a newer query has started */
            var ps = (d.resources && d.resources.results && d.resources.results.products) || [];
            var more = ps.length > LIMIT;
            render(ps.slice(0, LIMIT), q, more);
          })
          .catch(function (err) {
            if (err.name === 'AbortError' || mine !== seq) return;
            /* Don't dead-end. The suggest endpoint can fail for reasons that
               have nothing to do with the query - notably it returns 401 while
               the storefront is password protected - and the full search page
               still works. Offer it rather than stopping the customer here. */
            out.innerHTML = '<p class="csearch__msg">' + esc(CT('search_unavailable', 'Suggestions are unavailable right now.')) + '</p>' +
              '<a class="csearch__all" href="/search?q=' + encodeURIComponent(q) + '">' +
              esc(CT('search_search_for', 'Search for \u201c[query]\u201d').split('[query]').join(q)) + '</a>';
          });
      }, 220);
    });
  }

  /* ---------------- Collection sort + filters ---------------- */
  function initCollectionControls() {
    var sel = document.querySelector('[data-calmy-sort]');
    if (sel && !sel.__calmy) {
      sel.__calmy = true;
      sel.addEventListener('change', function () {
        var u = new URL(window.location.href);
        u.searchParams.set('sort_by', sel.value);
        u.searchParams.delete('page');
        window.location.href = u.toString();
      });
    }
    var form = document.querySelector('[data-calmy-filter-form]');
    if (form && !form.__calmy) {
      form.__calmy = true;
      var submit = function () {
        var params = new URLSearchParams();
        form.querySelectorAll('input').forEach(function (i) {
          if (i.type === 'checkbox') { if (i.checked) params.append(i.name, i.value); }
          else if (i.value !== '') params.append(i.name, i.value);
        });
        window.location.href = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      };
      form.addEventListener('change', function (e) { if (e.target.type === 'checkbox') submit(); });
      form.querySelectorAll('input[type=number]').forEach(function (i) { i.addEventListener('blur', submit); });
      form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    }
  }

  /* Every panel trigger gets aria-controls and aria-expanded, whether or not the markup
     declared them. Covers the collection filter drawer, which is rendered by a different
     section than the header. */
  var autoId = 0;
  function wireTriggers() {
    document.querySelectorAll('[data-calmy-open]').forEach(function (t) {
      var panel = document.querySelector(t.getAttribute('data-calmy-open'));
      if (!panel) return;
      if (!panel.id) { autoId += 1; panel.id = 'calmy-panel-' + autoId; }
      t.setAttribute('aria-controls', panel.id);
      if (!t.hasAttribute('aria-expanded')) {
        t.setAttribute('aria-expanded', panel.classList.contains('is-on') ? 'true' : 'false');
      }
      if (t.tagName === 'BUTTON' && !t.hasAttribute('aria-haspopup')) t.setAttribute('aria-haspopup', 'dialog');
    });
  }

  /* ---------------- Cart drawer ----------------
     Everything here comes from /cart.js. No sample lines, no development copy: an empty
     cart shows one sentence and one button, nothing more. Cross-sell is revealed only
     once the cart actually holds something.

     Row order is title, variant, then the quantity/price row, then Remove. Remove used to
     sit above the quantity control, which read as if it belonged to the variant line. */
  C.renderCart = function (cart, silent) {
    var d = document.querySelector('[data-calmy-cart]');
    if (!d) return;
    var body = d.querySelector('[data-calmy-cart-items]') || d.querySelector('[data-calmy-cart-body]');
    var foot = d.querySelector('[data-calmy-cart-foot]');
    var cross = d.querySelector('[data-calmy-cart-cross]');
    var shopUrl = d.dataset.shopUrl || '/collections/all';

    var headCount = d.querySelector('[data-calmy-cart-headcount]');
    if (headCount) {
      headCount.textContent = cart.item_count;
      headCount.hidden = cart.item_count === 0;
    }

    document.querySelectorAll('[data-calmy-cart-count]').forEach(function (el) {
      var sr = el.querySelector('.calmy-sr');
      el.textContent = cart.item_count;
      if (sr) el.insertBefore(sr, el.firstChild);
      el.setAttribute('data-empty', cart.item_count === 0 ? 'true' : 'false');
    });

    if (!cart.items || !cart.items.length) {
      /* The empty state is authored in Liquid - a mark, a line and two ways
         back in - so it is restored rather than rebuilt as a bare sentence. */
      if (body.__calmyEmpty) body.innerHTML = body.__calmyEmpty;
      foot.hidden = true;
      if (cross) cross.hidden = true;
      return;
    }
    if (cross) cross.hidden = false;
    foot.hidden = false;

    body.innerHTML = '<ul class="ccart__items">' + cart.items.map(function (it, i) {
      var opts = (it.options_with_values || [])
        .filter(function (o) { return o.value && o.value !== 'Default Title'; })
        .map(function (o) { return o.value; }).join(' \u00b7 ');
      var line = i + 1;
      var name = esc(it.product_title);
      var unit = it.quantity > 1 ? '<small>' + esc(C.money(it.final_price)) + ' each</small>' : '';

      /* Line item properties, shown under the variant.
         Something a customer typed - an engraving, a gift note - has to be
         visible where they can still change their mind about it. Shopify's
         underscore-prefixed keys are internal and stay hidden. */
      var props = Object.keys(it.properties || {})
        .filter(function (k) { return k.charAt(0) !== '_' && String(it.properties[k]).trim() !== ''; })
        .map(function (k) {
          return '<span class="ccart__prop"><b>' + esc(k) + '</b> ' + esc(it.properties[k]) + '</span>';
        }).join('');
      return '<li class="ccart__row">' +
        (it.image ? '<a class="ccart__thumb" href="' + esc(it.url) + '" tabindex="-1" aria-hidden="true"><img src="' + esc(withWidth(it.image, 260)) + '" alt="" loading="lazy" width="92" height="92"></a>' : '') +
        '<div class="ccart__info">' +
          /* Title and line total share the top row. Kept off the control row
             because at 390px a 44px stepper, a Remove link and a price do not
             fit on one line - they wrapped, and the price ended up floating
             on a line of its own under the controls. */
          '<div class="ccart__top">' +
            '<p class="ccart__t"><a href="' + esc(it.url) + '">' + name + '</a></p>' +
            '<span class="ccart__price">' + esc(C.money(it.final_line_price)) + unit + '</span>' +
          '</div>' +
          (opts ? '<p class="ccart__v">' + esc(opts) + '</p>' : '') +
          (props ? '<p class="ccart__props">' + props + '</p>' : '') +
          '<div class="ccart__foot2">' +
            '<span class="cqty">' +
              '<button type="button" aria-label="Decrease quantity of ' + name + '" data-calmy-line-qty="' + line + '" data-to="' + (it.quantity - 1) + '">&minus;</button>' +
              '<span>' + it.quantity + '</span>' +
              '<button type="button" aria-label="Increase quantity of ' + name + '" data-calmy-line-qty="' + line + '" data-to="' + (it.quantity + 1) + '">+</button>' +
            '</span>' +
            /* Remove sits on the same row as the stepper - it is a sibling
               action, not a separate decision needing its own line. */
            '<button type="button" class="ccart__rm" data-calmy-line-remove="' + line + '">' + esc(CT('cart_remove', 'Remove')) + '<span class="calmy-sr"> ' + name + '</span></button>' +
          '</div>' +
        '</div></li>';
    }).join('') + '</ul>';

    /* The checkout button repeats the subtotal, from this same cart object. */
    var ctaTotal = foot.querySelector('[data-calmy-cart-ctatotal]');
    if (ctaTotal) {
      if (cart.item_count > 0) {
        ctaTotal.textContent = '\u00b7 ' + C.money(cart.total_price);
        ctaTotal.hidden = false;
      } else { ctaTotal.hidden = true; }
    }
    var sub = foot.querySelector('[data-calmy-cart-subtotal]');
    if (sub) sub.textContent = C.money(cart.total_price);

    // Free-shipping progress. Threshold and copy both come from section
    // settings so the promise can be kept in step with Settings > Shipping.
    var ship = d.querySelector('[data-calmy-ship]');
    if (ship) {
      /* Two tiers on one track.

         Each tier owns its own segment, so the bar reads as two goals rather
         than one long run - the first fills to the shipping threshold, the
         second from there to the reward. Without a second threshold the first
         segment takes the whole track and nothing else renders, so a shop that
         only offers free shipping gets a plain bar.

         Both fills animate because they are transitioned in CSS on transform,
         so a quantity change slides rather than jumps. */
      var t1 = parseInt(ship.getAttribute('data-threshold'), 10) || 0;
      var t2 = parseInt(ship.getAttribute('data-threshold2'), 10) || 0;
      var total = cart.total_price;

      if (t1 > 0 && cart.item_count > 0) {
        var seg2 = ship.querySelector('[data-calmy-ship-seg2]');
        var f1 = ship.querySelector('[data-calmy-ship-fill1]');
        var f2 = ship.querySelector('[data-calmy-ship-fill2]');
        var msgEl = ship.querySelector('[data-calmy-ship-msg]');
        var bar = ship.querySelector('[data-calmy-ship-bar]');
        var twoTier = t2 > t1;

        var p1 = Math.max(0, Math.min(1, total / t1));
        var p2 = twoTier ? Math.max(0, Math.min(1, (total - t1) / (t2 - t1))) : 0;
        if (f1) f1.style.transform = 'scaleX(' + p1.toFixed(4) + ')';
        if (f2) f2.style.transform = 'scaleX(' + p2.toFixed(4) + ')';
        if (seg2) seg2.hidden = !twoTier;

        var label1 = ship.getAttribute('data-label1') || CT('cart_free_shipping', 'Free shipping');
        var label2 = ship.getAttribute('data-label2') || '';
        var line;
        if (total < t1) {
          line = (ship.getAttribute('data-msg-progress') || '[amount] to [tier]')
            .replace('[amount]', C.money(t1 - total)).replace('[tier]', label1);
        } else if (twoTier && total < t2) {
          /* First tier earned, second still ahead: name what has been unlocked
             and what is next in the same line, so the bar never needs a legend. */
          line = label1 + ' unlocked \u00b7 ' + C.money(t2 - total) + ' to ' + label2;
        } else {
          line = twoTier
            ? label1 + ' and ' + label2 + ' unlocked'
            : (ship.getAttribute('data-msg-reached') || label1 + ' unlocked');
        }
        if (msgEl) msgEl.textContent = line;

        var overall = twoTier ? Math.round(((p1 + p2) / 2) * 100) : Math.round(p1 * 100);
        if (bar) bar.setAttribute('aria-valuenow', String(overall));
        ship.hidden = false;
        ship.classList.toggle('is-reached', total >= (twoTier ? t2 : t1));
      } else {
        ship.hidden = true;
      }
    }

    if (!silent) announce(
      CT('cart_updated', 'Cart updated. [count] [items]. Subtotal [total].')
        .split('[count]').join(cart.item_count)
        .split('[items]').join(cart.item_count === 1 ? CT('cart_item_one', 'item') : CT('cart_item_other', 'items'))
        .split('[total]').join(C.money(cart.total_price))
    );
  };

  C.refreshCart = function (openAfter) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Under Kaching the drawer does not exist, so there is nothing to
           render into and nothing to open. The header count is still ours and
           still has to be right, so that alone is updated. Opening the app's
           cart is the app's decision, made from its own settings - the theme
           does not reach into it. */
        if (!C.usesThemeCart) { C.setCartCount(cart.item_count); return cart; }
        C.renderCart(cart, !openAfter);
        if (openAfter) C.open('[data-calmy-cart]');
        return cart;
      })
      .catch(function () {});
  };

  /* The header badge, on its own. Used when no drawer is being rendered. */
  C.setCartCount = function (n) {
    document.querySelectorAll('[data-calmy-cart-count]').forEach(function (el) {
      var sr = el.querySelector('.calmy-sr');
      el.textContent = '';
      if (sr) el.appendChild(sr);
      el.appendChild(document.createTextNode(n > 0 ? String(n) : (sr ? '0' : '')));
      if (el.hasAttribute('data-empty')) el.setAttribute('data-empty', n > 0 ? 'false' : 'true');
    });

    /* THE ACCESSIBLE NAME CARRIES THE NUMBER.

       Every cart control has an aria-label, and an aria-label replaces the
       element's contents for a screen reader - so the visually hidden count
       inside the badge was never read out. The label is rebuilt from the two
       strings Liquid put on the element, so it stays translatable and matches
       what the page rendered on the server. */
    document.querySelectorAll('[data-calmy-cart-btn]').forEach(function (b) {
      var base = b.getAttribute('data-cart-label') || 'Cart';
      var word = b.getAttribute('data-cart-count-label') || '';
      b.setAttribute('aria-label', n > 0 ? base + ' \u2014 ' + word + ' ' + n : base);
    });
  };

  /* ══ THE BADGE FOLLOWS THE REAL CART ══════════════════════════════════════
     ONE SOURCE OF TRUTH: /cart.js, and its item_count. Nothing here adds up
     line items, and nothing keeps a running tally that could drift - a bundle
     that puts three things in the cart reports three because Shopify says
     three, not because the theme worked it out.

     WHY THIS HOOKS fetch AND XHR. The count used to be refreshed only by the
     theme's own add-to-cart paths, so anything else changing the cart left the
     badge stale: Kaching Bundles adds through its own request, and its drawer
     changes quantities and removes lines without telling the theme. Rather
     than guess at each app's events, this notices the REQUEST - every cart
     mutation on a Shopify storefront is a call to /cart/add, /cart/change,
     /cart/update or /cart/clear - and re-reads the cart once it has settled.

     Both hooks WRAP what is already there and call it. Kaching patches fetch
     too; wrapping keeps whatever it did intact. */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var pending = null;

  C.refreshCartCount = function () {
    /* Coalesced: a drawer that fires three changes in a row causes one read. */
    if (pending) return pending;
    pending = fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { C.setCartCount(cart.item_count); return cart; })
      .catch(function () { /* leave the badge as it is rather than zero it */ })
      .then(function (v) { pending = null; return v; });
    return pending;
  };

  function watchCartWrites() {
    if (window.__calmyCartWatch) return;
    window.__calmyCartWatch = true;

    var url = function (x) {
      try { return typeof x === 'string' ? x : (x && x.url) || ''; } catch (e) { return ''; }
    };

    if (window.fetch) {
      var of = window.fetch;
      window.fetch = function (input, init) {
        var u = url(input);
        var p = of.apply(this, arguments);
        if (CART_WRITE.test(u)) {
          /* After it settles, whether it succeeded or not: a rejected change
             leaves the cart as it was, and re-reading is still correct. */
          p.then(function () { C.refreshCartCount(); }, function () { C.refreshCartCount(); });
        }
        return p;
      };
    }

    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var oo = XHR.prototype.open, os = XHR.prototype.send;
      XHR.prototype.open = function (m, u) { this.__calmyCartUrl = u || ''; return oo.apply(this, arguments); };
      XHR.prototype.send = function () {
        var self = this;
        if (CART_WRITE.test(self.__calmyCartUrl || '')) {
          self.addEventListener('loadend', function () { C.refreshCartCount(); });
        }
        return os.apply(this, arguments);
      };
    }

    /* Coming back through the bfcache repaints the old DOM without a request,
       so the badge would show whatever it said when the page was left. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) C.refreshCartCount(); });
  }
  watchCartWrites();

  var busy = false;
  function changeLine(line, qty) {
    if (busy) return;
    busy = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { C.renderCart(cart); })
      .catch(function () { announce(CT('cart_error', 'Could not update the cart.')); })
      .finally(function () { busy = false; });
  }

  document.addEventListener('click', function (e) {
    var q = e.target.closest && e.target.closest('[data-calmy-line-qty]');
    if (q) {
      var to = parseInt(q.getAttribute('data-to'), 10);
      if (isNaN(to) || to < 0) return;
      changeLine(parseInt(q.getAttribute('data-calmy-line-qty'), 10), to);
      return;
    }
    var rm = e.target.closest && e.target.closest('[data-calmy-line-remove]');
    if (rm) changeLine(parseInt(rm.getAttribute('data-calmy-line-remove'), 10), 0);
  });

  /* ---------------- Boot ---------------- */
  function boot() {
    portal();
    /* Closed panels start inert so their links are never keyboard reachable. */
    document.querySelectorAll('.cpanel, .csearch, .cacct').forEach(function (p) {
      if (!p.classList.contains('is-on')) p.setAttribute('inert', '');
    });
    var d = document.querySelector('[data-calmy-cart]');
    if (d && d.dataset.moneyFormat) C.moneyFormat = d.dataset.moneyFormat;
    wireTriggers();
    /* Predictive search moved to assets/calmy-search.js in round 3 — grouped
       results, keyboard traversal and recent searches outgrew this file. The
       function below is left in place but no longer called, so nothing binds
       twice; delete it once the new one has shipped a while. */
    // initSearch();
    initCollectionControls();
    if (d && !d.__calmyHydrated) {
      d.__calmyHydrated = true;
      var items = d.querySelector('[data-calmy-cart-items]');
      if (items) items.__calmyEmpty = items.innerHTML;
      C.refreshCart(false);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();

/* ---------------- Header: scrolled state ----------------
   The wordmark shows its tagline at the top of a desktop page and drops to the
   word alone once the header is stuck to the top edge. A sentinel above the
   header does the detecting, so nothing measures scroll position on every
   frame - the observer fires twice per page, once each way. */
(function () {
  var head = document.querySelector('.chead');
  if (!head || !('IntersectionObserver' in window)) return;

  var sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none';
  if (head.parentNode) head.parentNode.insertBefore(sentinel, head);

  new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { head.classList.toggle('is-stuck', !e.isIntersecting); });
  }, { threshold: 0 }).observe(sentinel);
})();
